'use client'

import { useState, useEffect } from 'react'
import { ColumnDef } from "@tanstack/react-table"
import { ArrowUpDown, MoreHorizontal, Trash, ExternalLink, Download, FileText, FileCheck, Ban, CreditCard, CheckCircle2, Clock, AlertTriangle, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import FormModal from "../../components/FormModal"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import InvoiceService from '@/api/invoices.service'
import PaymentMethodService from '@/api/paymentmethods.service'
import ChartOfAccountsService from '@/api/chartofaccounts.service'
import TransactionsService from '@/api/transactions.service'
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow, format, parseISO, isPast, isAfter } from 'date-fns'

export type InvoiceType = {
  id: number
  publicId: string
  invoiceNumber: string
  amount: number
  currency: string
  status: string
  issueDate: string
  dueDate: string
  dateUpdated: string
  dateCreated: string
  receipts: any[]
}

export type InvoiceItemType = {
  id: number
  invoiceId: number
  productServiceId: number
  description: string
  quantity: number
  unitPrice: number
  totalPrice: number
}

export type InvoiceTaxType = {
  id: number
  invoiceId: number
  taxName: string
  taxRate: number
  taxAmount: number
}

const formatCurrency = (amount: number, currency: string) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  });
  
  return formatter.format(amount);
};

const getStatusBadge = (status: string) => {
  switch (status.toLowerCase()) {
    case 'paid':
      return <Badge variant="paid" className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Paid</Badge>;
    case 'pending':
      return <Badge variant="pending" className="flex items-center gap-1"><Clock className="h-3 w-3" /> Pending</Badge>;
    case 'overdue':
      return <Badge variant="overdue" className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Overdue</Badge>;
    case 'partially_paid':
      return <Badge variant="pending" className="flex items-center gap-1"><Clock className="h-3 w-3" /> Partially Paid</Badge>;
    case 'draft':
      return <Badge variant="draft" className="flex items-center gap-1"><FileText className="h-3 w-3" /> Draft</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

export const columns: ColumnDef<InvoiceType>[] = [
  {
    accessorKey: "invoiceNumber",
    header: ({ column }: { column: any }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="pl-0 font-medium text-sm"
        >
          Invoice Number
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const receipts = row.original.receipts;
      return (
        <div className="font-medium text-sm">
          <a 
            href={`/invoice/${row.original.id}`} 
            className="text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1"
          >
            {row.original.invoiceNumber}
            <ExternalLink className="h-3 w-3 ml-1" />
          </a>
          {receipts && receipts.length > 0 && (
            <div className="mt-2 flex flex-col gap-1">
              {receipts.map((receipt: any) => (
                <Button 
                  key={receipt.id} 
                  onClick={() => window.open(`/receipt/${receipt.id}`, '_blank')} 
                  variant="outline" 
                  size="sm"
                  className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 border-green-200 hover:bg-green-50 dark:hover:bg-green-900/20"
                >
                  <FileText className="h-3 w-3" />
                  Receipt {receipt.receiptNumber} ({formatCurrency(receipt.amount, receipt.currency)})
                  <ExternalLink className="h-3 w-3 ml-1" />
                </Button>
              ))}
            </div>
          )}
        </div>
      );
    },
    filterFn: (row, id, value) => {
      const invoiceNumber = row.getValue(id);
      if (typeof invoiceNumber === 'string') {
        return invoiceNumber.toLowerCase().includes(value.toLowerCase());
      }
      return String(invoiceNumber).toLowerCase().includes(value.toLowerCase());
    },
  },
  {
    accessorKey: "status",
    header: ({ column }: { column: any }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="pl-0 font-medium text-sm"
        >
          Status
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return getStatusBadge(row.original.status);
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: "amount",
    header: ({ column }: { column: any }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="pl-0 font-medium text-sm"
        >
          Amount
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return (
        <div className="font-medium text-right">
          {formatCurrency(row.original.amount, row.original.currency)}
        </div>
      );
    },
  },
  {
    accessorKey: "issueDate",
    header: ({ column }: { column: any }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="pl-0 font-medium text-sm"
        >
          Issue Date
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
    cell: ({ row }) => {
      try {
        const date = parseISO(row.original.issueDate);
        return (
          <div className="text-sm">
            <div>{format(date, 'MMM d, yyyy')}</div>
            <div className="text-muted-foreground text-xs">
              {formatDistanceToNow(date, { addSuffix: true })}
            </div>
          </div>
        );
      } catch (e) {
        return row.original.issueDate;
      }
    },
  },
  {
    accessorKey: "dueDate",
    header: ({ column }: { column: any }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="pl-0 font-medium text-sm"
        >
          Due Date
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
    cell: ({ row }) => {
      try {
        const date = parseISO(row.original.dueDate);
        const isPastDue = isPast(date) && row.original.status !== 'paid';
        
        return (
          <div className="text-sm">
            <div className={isPastDue ? "text-rose-600 font-medium" : ""}>
              {format(date, 'MMM d, yyyy')}
            </div>
            <div className={`text-xs ${isPastDue ? "text-rose-600" : "text-muted-foreground"}`}>
              {formatDistanceToNow(date, { addSuffix: true })}
            </div>
          </div>
        );
      } catch (e) {
        return row.original.dueDate;
      }
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const invoice = row.original;
      const { toast } = useToast();
      const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
      const [showMarkAsPartiallyPaidModal, setShowMarkAsPartiallyPaidModal] = useState(false);
      const [paymentAmount, setPaymentAmount] = useState('');
      const [paymentMethodId, setPaymentMethodId] = useState('');
      const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
      const [isLoading, setIsLoading] = useState(false);
      const [paymentDate, setPaymentDate] = useState('');
      const [paymentReference, setPaymentReference] = useState('');
      const [paymentAccount, setPaymentAccount] = useState('');
      const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null);
      
      // Chart of accounts state
      const [chartOfAccounts, setChartOfAccounts] = useState<any[]>([]);
      const [selectedAccountId, setSelectedAccountId] = useState('');
      const [isChartOfAccountsLoaded, setIsChartOfAccountsLoaded] = useState(false);
      
      const [selectedBusiness] = useState(() => {
        const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
        return storedBusiness ? JSON.parse(storedBusiness) : null;
      });

      const fetchPaymentMethods = async () => {
        try {
          const response = await PaymentMethodService.getAllPaymentMethods();
          if (response.data.result === 'success') {
            setPaymentMethods(response.data.paymentMethods);
          }
        } catch (error) {
          console.error('Failed to fetch payment methods:', error);
        }
      };
      
      // Function to fetch chart of accounts
      const fetchChartOfAccounts = async () => {
        try {
          if (!selectedBusiness) return;
          const response = await ChartOfAccountsService.getAllChartOfAccounts(selectedBusiness.id);
          if (response.data.result === 'success') {
            // Filter to include only active accounts
            const activeAccounts = response.data.chartOfAccounts.filter(
              (account: any) => account.isActive
            );
            setChartOfAccounts(activeAccounts);
          } else {
            toast({
              title: "Error",
              description: "Failed to fetch chart of accounts",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error("Error fetching chart of accounts:", error);
          toast({
            title: "Error",
            description: "Failed to fetch chart of accounts",
            variant: "destructive",
          });
        } finally {
          setIsChartOfAccountsLoaded(true);
        }
      };

      useEffect(() => {
        if (showMarkAsPaidModal || showMarkAsPartiallyPaidModal) {
          fetchPaymentMethods();
          fetchChartOfAccounts();
          // Set default payment date to today
          setPaymentDate(format(new Date(), 'yyyy-MM-dd'));
        }
      }, [showMarkAsPaidModal, showMarkAsPartiallyPaidModal]);

      useEffect(() => {
        if (paymentMethodId) {
          const method = paymentMethods.find(m => m.id.toString() === paymentMethodId);
          setSelectedPaymentMethod(method);
        } else {
          setSelectedPaymentMethod(null);
        }
      }, [paymentMethodId, paymentMethods]);

      const updateInvoiceStatus = async (status: string, amount: any) => {
        setIsLoading(true);
        try {
          const payload = {
            status: status,
            paymentMethodId: parseInt(paymentMethodId, 10),
            amount: amount || invoice.amount,
            paymentDate: paymentDate,
            paymentReference: paymentReference,
            paymentAccount: paymentAccount
          };
          
          const res = await InvoiceService.updateSpecificInvoice(invoice.id, payload);
          
          if (res.data.result === 'success') {
            // Record the transaction if an account is selected
            if (selectedAccountId) {
              await recordTransaction(status, amount || invoice.amount, res.data.receiptNumber, res.data.receiptId);
            }
            
            toast({
              title: "Success",
              description: "Invoice status updated successfully.",
            });
            window.location.reload();
          } else {
            toast({
              title: "Error",
              description: res.data.message,
              variant: "destructive"
            });
          }
        } catch (error) {
          toast({
            title: "Error",
            description: "An error occurred while updating invoice status.",
            variant: "destructive"
          });
        } finally {
          setIsLoading(false);
          setShowMarkAsPaidModal(false);
          setShowMarkAsPartiallyPaidModal(false);
        }
      };
      
      // Function to record transaction
      const recordTransaction = async (status: string, amount: any, receiptNumber: string, receiptId: string) => {
        try {
          // Convert to number and ensure it's a valid value
          const amountValue = typeof amount === 'string' ? parseFloat(amount) : amount;
          
          const transactionData = {
            chartOfAccountId: parseInt(selectedAccountId),
            transactionType: "CREDIT", // Receipt is always a credit to the selected account
            amount: amountValue,
            currency: invoice.currency,
            description: `Payment received for Invoice #${invoice.invoiceNumber}`,
            reference: receiptNumber,
            referenceId: receiptId,
            referenceType: "receipt",
            transactionDate: paymentDate,
          };
          
          const response = await TransactionsService.createTransaction(transactionData, selectedBusiness.id);
          
          if (response.data.result === 'success') {
            toast({
              title: "Success",
              description: "The payment has been recorded in the accounting system.",
            });
          } else {
            toast({
              title: "Warning",
              description: "Invoice updated but transaction recording failed: " + response.data.message,
              variant: "destructive"
            });
          }
        } catch (error) {
          console.error("Error recording transaction:", error);
          toast({
            title: "Warning",
            description: "Invoice updated but transaction recording failed.",
            variant: "destructive"
          });
        }
      };

      const handleMarkAsPaid = () => {
        setShowMarkAsPaidModal(true);
      };

      const handleMarkAsPartiallyPaid = () => {
        setShowMarkAsPartiallyPaidModal(true);
      };

      const needsAccountDetails = selectedPaymentMethod && 
        (selectedPaymentMethod.name === 'Bank Transfer' || selectedPaymentMethod.name === 'Mobile Money');

      return (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="cursor-pointer" 
                onClick={() => window.location.href = `/invoice/${invoice.id}`}
              >
                <FileText className="mr-2 h-4 w-4" />
                <span>View Details</span>
              </DropdownMenuItem>
              
              {(invoice.status === 'pending' || invoice.status === 'overdue') && (
                <>
                  <DropdownMenuItem 
                    className="cursor-pointer" 
                    onClick={handleMarkAsPaid}
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    <span>Mark as Paid</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="cursor-pointer" 
                    onClick={handleMarkAsPartiallyPaid}
                  >
                    <FileCheck className="mr-2 h-4 w-4" />
                    <span>Partial Payment</span>
                  </DropdownMenuItem>
                </>
              )}
              
              {(invoice.status === 'pending' || invoice.status === 'overdue') && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="cursor-pointer text-destructive focus:text-destructive" 
                    onClick={() => {
                      if (window.confirm("Are you sure you want to delete this invoice? This action cannot be undone.")) {
                        InvoiceService.deleteInvoice(invoice.id)
                          .then(() => {
                            toast({ description: "Invoice deleted successfully" });
                            window.location.reload();
                          })
                          .catch(() => toast({ description: "Failed to delete invoice", variant: "destructive" }));
                      }
                    }}
                  >
                    <Trash className="mr-2 h-4 w-4" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mark as Paid Modal */}
          <FormModal
            open={showMarkAsPaidModal}
            setOpen={setShowMarkAsPaidModal}
            title="Mark Invoice as Paid"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Payment Method</label>
                <select
                  className="w-full p-2 border rounded"
                  value={paymentMethodId}
                  onChange={(e) => setPaymentMethodId(e.target.value)}
                  required
                >
                  <option value="">Select a payment method</option>
                  {paymentMethods.map((method) => (
                    <option key={method.id} value={method.id}>{method.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Date</label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Reference</label>
                <Input
                  type="text"
                  placeholder="Enter payment reference"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                />
              </div>
              {needsAccountDetails && (
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Account</label>
                  <Input
                    type="text"
                    placeholder={`Enter ${selectedPaymentMethod.name === 'Bank Transfer' ? 'bank account' : 'mobile money'} details`}
                    value={paymentAccount}
                    onChange={(e) => setPaymentAccount(e.target.value)}
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Chart of Account</label>
                {isChartOfAccountsLoaded ? (
                  <select
                    value={selectedAccountId}
                    onChange={(e) => setSelectedAccountId(e.target.value)}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select Account (Optional)</option>
                    {chartOfAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.code} - {account.name} ({account.type})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="flex justify-center items-center">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading accounts...
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Select an account to record this payment as a transaction
                </p>
              </div>
              <div className="flex justify-end mt-4">
                <Button 
                  variant="paid" 
                  onClick={() => updateInvoiceStatus('paid', null)}
                  isLoading={isLoading}
                  className="mr-2"
                  disabled={!paymentMethodId || !paymentDate}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </FormModal>

          {/* Mark as Partially Paid Modal */}
          <FormModal
            open={showMarkAsPartiallyPaidModal}
            setOpen={setShowMarkAsPartiallyPaidModal}
            title="Record Partial Payment"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Payment Method</label>
                <select
                  className="w-full p-2 border rounded"
                  value={paymentMethodId}
                  onChange={(e) => setPaymentMethodId(e.target.value)}
                  required
                >
                  <option value="">Select a payment method</option>
                  {paymentMethods.map((method) => (
                    <option key={method.id} value={method.id}>{method.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Amount Paid</label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  min="0.01"
                  max={invoice.amount.toString()}
                  step="0.01"
                  required
                />
                <div className="text-xs text-muted-foreground mt-1">
                  Invoice Total: {formatCurrency(invoice.amount, invoice.currency)}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Date</label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Reference</label>
                <Input
                  type="text"
                  placeholder="Enter payment reference"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                />
              </div>
              {needsAccountDetails && (
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Account</label>
                  <Input
                    type="text"
                    placeholder={`Enter ${selectedPaymentMethod.name === 'Bank Transfer' ? 'bank account' : 'mobile money'} details`}
                    value={paymentAccount}
                    onChange={(e) => setPaymentAccount(e.target.value)}
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Chart of Account</label>
                {isChartOfAccountsLoaded ? (
                  <select
                    value={selectedAccountId}
                    onChange={(e) => setSelectedAccountId(e.target.value)}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select Account (Optional)</option>
                    {chartOfAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.code} - {account.name} ({account.type})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="flex justify-center items-center">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading accounts...
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Select an account to record this payment as a transaction
                </p>
              </div>
              <div className="flex justify-end mt-4">
                <Button 
                  variant="pending" 
                  onClick={() => updateInvoiceStatus('partially_paid', parseFloat(paymentAmount))}
                  isLoading={isLoading}
                  className="mr-2"
                  disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || !paymentMethodId || !paymentDate}
                >
                  Confirm Payment
                </Button>
              </div>
            </div>
          </FormModal>
        </>
      );
    },
  },
];
